import { IconRemove } from '@newfront-insurance/core-ui';
import { Button } from '@newfront-insurance/core-ui/v2';
import { useState } from 'react';

import { SkillGroupShareDialog } from './skill-group-share-dialog';
import { useCustomSkillGroupShares } from './use-custom-skill-group-shares';
import type { CustomSkillRecord } from '../../../../../queries/chats';
import type { SkillShareGroup } from '../../../../../queries/skill-group-sharing';
import { SettingsConfirmationDialog } from '../../settings-confirmation-dialog';
import { SettingsSectionList, SettingsSectionListRow } from '../../settings-section-list';
import { SettingsActionButton } from '../../settings-shared/settings-action-button';

export function CustomSkillGroupSharesSummary({
  skill,
  onChanged,
}: {
  skill: CustomSkillRecord;
  onChanged: () => Promise<void>;
}) {
  const [isSelectingGroups, setIsSelectingGroups] = useState(false);
  const [groupToRemove, setGroupToRemove] = useState<SkillShareGroup | null>(null);
  const {
    groups,
    isInitialLoading,
    isSaving,
    removingGroupUuid,
    removalError,
    clearRemovalError,
    error,
    reload,
    addGroups,
    removeGroup,
  } = useCustomSkillGroupShares(skill, onChanged);

  return (
    <section aria-label="Teams with skill access" className="space-y-3">
      {error ? (
        <p role="alert" className="text-fire-500 text-sm">
          Couldn't load the groups sharing this skill.{' '}
          <Button type="button" variant="inline" onClick={() => void reload().catch(() => undefined)}>
            Retry
          </Button>
        </p>
      ) : null}
      <SettingsSectionList
        title="Shared with teams"
        loading={isInitialLoading}
        emptyMessage={
          !groups.length && !isInitialLoading && !error
            ? 'No teams or organizations have access to this skill.'
            : undefined
        }
        headerEnd={
          <Button
            type="button"
            variant="tertiary"
            className="h-auto shrink-0 p-0 text-sm leading-4"
            disabled={isInitialLoading || isSaving || Boolean(error)}
            onClick={() => setIsSelectingGroups(true)}
          >
            + Add teams
          </Button>
        }
      >
        {isInitialLoading
          ? [0, 1].map((index) => <SettingsSectionListRow key={index} loadingLabel="Loading shared group" />)
          : groups.map((group) => (
              <SettingsSectionListRow
                key={group.uuid}
                title={group.name}
                description={group.isOrganization ? 'Organization' : 'Team'}
                trailing={
                  <SettingsActionButton
                    label={`Unshare from ${group.name}`}
                    tooltip="Unshare"
                    disabled={isSaving}
                    ariaBusy={removingGroupUuid === group.uuid}
                    onClick={() => {
                      clearRemovalError();
                      setGroupToRemove(group);
                    }}
                  >
                    <span aria-hidden="true">
                      <IconRemove color="currentColor" size={16} />
                    </span>
                  </SettingsActionButton>
                }
              />
            ))}
      </SettingsSectionList>
      {groupToRemove ? (
        <SettingsConfirmationDialog
          title="Revoke skill access"
          description={
            <>
              Stop sharing /{skill.command} with {groupToRemove.name}? The skill and its other shares will be kept.
            </>
          }
          confirmLabel="Revoke access"
          pendingLabel="Revoking…"
          pending={isSaving}
          error={removalError}
          onClose={() => setGroupToRemove(null)}
          onConfirm={() =>
            void removeGroup(groupToRemove).then((succeeded) => {
              if (succeeded) setGroupToRemove(null);
            })
          }
        />
      ) : null}
      {isSelectingGroups ? (
        <SkillGroupShareDialog sharedGroups={groups} onClose={() => setIsSelectingGroups(false)} onShare={addGroups} />
      ) : null}
    </section>
  );
}
